import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Building2, Star, MessageSquare, Edit, Eye, TrendingUp, 
  ArrowLeft, Clock, Plus, Trash2
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { StarRating, RatingDisplay } from "../components/StarRating";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProviderDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [respondDialogOpen, setRespondDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    website: "",
    hours: "",
    image_url: "",
  });

  const [responseText, setResponseText] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const servicesRes = await axios.get(`${API}/my-services`, { withCredentials: true });
        
        if (!servicesRes.data || servicesRes.data.length === 0) {
          // No services, redirect to create
          navigate("/create-listing");
          return;
        }

        setServices(servicesRes.data);
        // Select first service by default
        const firstService = servicesRes.data[0];
        setSelectedService(firstService);
        loadServiceDetails(firstService);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const loadServiceDetails = async (service) => {
    setEditForm({
      name: service.name || "",
      description: service.description || "",
      phone: service.phone || "",
      email: service.email || "",
      address: service.address || "",
      city: service.city || "",
      website: service.website || "",
      hours: service.hours || "",
      image_url: service.image_url || "",
    });

    // Fetch reviews for this service
    try {
      const reviewsRes = await axios.get(`${API}/reviews/${service.service_id}`);
      setReviews(reviewsRes.data);
    } catch (error) {
      setReviews([]);
    }
  };

  const handleSelectService = (service) => {
    setSelectedService(service);
    loadServiceDetails(service);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedService) return;

    try {
      const res = await axios.put(
        `${API}/services/${selectedService.service_id}`,
        editForm,
        { withCredentials: true }
      );
      
      // Update in list
      setServices(services.map(s => 
        s.service_id === selectedService.service_id ? res.data : s
      ));
      setSelectedService(res.data);
      setEditDialogOpen(false);
      toast.success("Listing updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    }
  };

  const handleDeleteService = async (serviceId) => {
    try {
      await axios.delete(`${API}/services/${serviceId}`, { withCredentials: true });
      
      const remaining = services.filter(s => s.service_id !== serviceId);
      setServices(remaining);
      
      if (remaining.length > 0) {
        setSelectedService(remaining[0]);
        loadServiceDetails(remaining[0]);
      } else {
        navigate("/create-listing");
      }
      
      toast.success("Listing deleted");
    } catch (error) {
      toast.error("Failed to delete listing");
    }
  };

  const handleRespondSubmit = async (e) => {
    e.preventDefault();
    if (!selectedReview) return;

    try {
      const res = await axios.put(
        `${API}/reviews/${selectedReview.review_id}/respond`,
        { response: responseText },
        { withCredentials: true }
      );
      
      setReviews(reviews.map((r) => 
        r.review_id === selectedReview.review_id ? res.data : r
      ));
      setRespondDialogOpen(false);
      setSelectedReview(null);
      setResponseText("");
      toast.success("Response submitted!");
    } catch (error) {
      toast.error("Failed to submit response");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7]">
        <Header user={user} />
        <div className="pt-24 flex items-center justify-center h-[60vh]">
          <div className="w-12 h-12 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="provider-dashboard">
      <Header user={user} />

      <main className="pt-24 lg:pt-28 pb-16 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#1A4D2E] mb-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Link>
              <h1 className="text-2xl font-bold text-[#1A1A1A]">My Listings</h1>
            </div>
            <Button
              asChild
              className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white rounded-full"
            >
              <Link to="/create-listing" data-testid="add-listing-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add New Listing
              </Link>
            </Button>
          </div>

          {/* Listings Grid */}
          {services.length > 1 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
              {services.map((service) => (
                <button
                  key={service.service_id}
                  onClick={() => handleSelectService(service)}
                  className={`p-4 rounded-xl border text-left transition-all ${
                    selectedService?.service_id === service.service_id
                      ? "border-[#1A4D2E] bg-[#1A4D2E]/5 ring-2 ring-[#1A4D2E]/20"
                      : "border-stone-200 bg-white hover:border-[#1A4D2E]/30"
                  }`}
                  data-testid={`listing-btn-${service.service_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center overflow-hidden">
                      {service.image_url ? (
                        <img src={service.image_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Building2 className="w-5 h-5 text-stone-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-[#1A1A1A] truncate text-sm">{service.name}</p>
                      <p className="text-xs text-[#71717A]">{service.review_count || 0} reviews</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Selected Service Details */}
          {selectedService && (
            <>
              {/* Service Overview Card */}
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden mb-8">
                <div className="relative h-48 bg-stone-100">
                  {selectedService.image_url ? (
                    <img
                      src={selectedService.image_url}
                      alt={selectedService.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1A4D2E]/10 to-[#1A4D2E]/5">
                      <Building2 className="w-16 h-16 text-[#1A4D2E]/30" />
                    </div>
                  )}
                  {/* Status Badge */}
                  <div className="absolute top-4 left-4 flex gap-2">
                    {selectedService.is_approved ? (
                      <Badge className="bg-green-500 text-white border-0">Approved</Badge>
                    ) : (
                      <Badge className="bg-amber-500 text-white border-0">Pending</Badge>
                    )}
                    {selectedService.is_featured && (
                      <Badge className="bg-[#E07A5F] text-white border-0">Featured</Badge>
                    )}
                  </div>
                </div>

                <div className="p-6 lg:p-8">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-[#1A1A1A] mb-2">{selectedService.name}</h2>
                      <RatingDisplay
                        rating={selectedService.average_rating || 0}
                        reviewCount={selectedService.review_count || 0}
                      />
                      <p className="text-[#4A4A4A] mt-4">{selectedService.description}</p>
                    </div>

                    <div className="flex gap-3">
                      <Button
                        asChild
                        variant="outline"
                        className="border-[#1A4D2E] text-[#1A4D2E] rounded-full"
                      >
                        <Link to={`/service/${selectedService.service_id}`}>
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </Link>
                      </Button>

                      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                        <DialogTrigger asChild>
                          <Button className="bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full">
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>Edit Listing</DialogTitle>
                          </DialogHeader>
                          <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="name">Business Name</Label>
                                <Input
                                  id="name"
                                  value={editForm.name}
                                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                  required
                                />
                              </div>
                              <div>
                                <Label htmlFor="phone">Phone</Label>
                                <Input
                                  id="phone"
                                  value={editForm.phone}
                                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                                  required
                                />
                              </div>
                            </div>
                            <div>
                              <Label htmlFor="email">Email</Label>
                              <Input
                                id="email"
                                type="email"
                                value={editForm.email}
                                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                required
                              />
                            </div>
                            <div>
                              <Label htmlFor="description">Description</Label>
                              <Textarea
                                id="description"
                                value={editForm.description}
                                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                required
                              />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="address">Address</Label>
                                <Input
                                  id="address"
                                  value={editForm.address}
                                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                                  required
                                />
                              </div>
                              <div>
                                <Label htmlFor="city">City</Label>
                                <Input
                                  id="city"
                                  value={editForm.city}
                                  onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                                  required
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="website">Website</Label>
                                <Input
                                  id="website"
                                  value={editForm.website}
                                  onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                                />
                              </div>
                              <div>
                                <Label htmlFor="hours">Hours</Label>
                                <Input
                                  id="hours"
                                  value={editForm.hours}
                                  onChange={(e) => setEditForm({ ...editForm, hours: e.target.value })}
                                />
                              </div>
                            </div>
                            <div>
                              <Label htmlFor="image_url">Image URL</Label>
                              <Input
                                id="image_url"
                                value={editForm.image_url}
                                onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })}
                              />
                            </div>
                            <div className="flex gap-3 pt-4">
                              <Button type="submit" className="bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full">
                                Save Changes
                              </Button>
                              <Button type="button" variant="ghost" onClick={() => setEditDialogOpen(false)}>
                                Cancel
                              </Button>
                            </div>
                          </form>
                        </DialogContent>
                      </Dialog>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" className="text-red-500 hover:text-red-600 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Listing?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete "{selectedService.name}" and all reviews.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteService(selectedService.service_id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-8 border-t border-stone-100">
                    <div className="text-center p-4 bg-stone-50 rounded-xl">
                      <Star className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-[#1A1A1A]">
                        {selectedService.average_rating?.toFixed(1) || "0.0"}
                      </p>
                      <p className="text-sm text-[#71717A]">Rating</p>
                    </div>
                    <div className="text-center p-4 bg-stone-50 rounded-xl">
                      <MessageSquare className="w-6 h-6 text-[#1A4D2E] mx-auto mb-2" />
                      <p className="text-2xl font-bold text-[#1A1A1A]">{selectedService.review_count || 0}</p>
                      <p className="text-sm text-[#71717A]">Reviews</p>
                    </div>
                    <div className="text-center p-4 bg-stone-50 rounded-xl">
                      <TrendingUp className="w-6 h-6 text-[#E07A5F] mx-auto mb-2" />
                      <p className="text-2xl font-bold text-[#1A1A1A]">
                        {selectedService.is_featured ? "Yes" : "No"}
                      </p>
                      <p className="text-sm text-[#71717A]">Featured</p>
                    </div>
                    <div className="text-center p-4 bg-stone-50 rounded-xl">
                      <Clock className="w-6 h-6 text-[#4A4A4A] mx-auto mb-2" />
                      <p className="text-2xl font-bold text-[#1A1A1A]">
                        {new Date(selectedService.created_at).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-[#71717A]">Listed</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reviews Section */}
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 lg:p-8">
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-6">
                  Reviews ({reviews.length})
                </h3>

                {reviews.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="w-12 h-12 text-stone-200 mx-auto mb-4" />
                    <p className="text-[#71717A]">No reviews yet</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {reviews.map((review) => (
                      <div key={review.review_id} className="border-b border-stone-100 pb-6 last:border-0">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-medium text-[#1A1A1A]">{review.user_name}</p>
                            <StarRating rating={review.rating} size="sm" />
                          </div>
                          <span className="text-sm text-[#71717A]">
                            {new Date(review.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-[#4A4A4A] mb-4">{review.comment}</p>

                        {review.provider_response ? (
                          <div className="ml-4 pl-4 border-l-2 border-[#1A4D2E]/20">
                            <p className="text-sm font-medium text-[#1A4D2E] mb-1">Your Response:</p>
                            <p className="text-[#4A4A4A] text-sm">{review.provider_response}</p>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedReview(review);
                              setRespondDialogOpen(true);
                            }}
                            className="border-[#1A4D2E] text-[#1A4D2E]"
                          >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Respond
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Respond Dialog */}
                <Dialog open={respondDialogOpen} onOpenChange={setRespondDialogOpen}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Respond to Review</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleRespondSubmit} className="space-y-4 mt-4">
                      {selectedReview && (
                        <div className="p-4 bg-stone-50 rounded-lg">
                          <p className="font-medium text-[#1A1A1A] mb-1">{selectedReview.user_name}</p>
                          <StarRating rating={selectedReview.rating} size="sm" />
                          <p className="text-[#4A4A4A] mt-2 text-sm">{selectedReview.comment}</p>
                        </div>
                      )}
                      <div>
                        <Label htmlFor="response">Your Response</Label>
                        <Textarea
                          id="response"
                          value={responseText}
                          onChange={(e) => setResponseText(e.target.value)}
                          placeholder="Thank the customer or address their feedback..."
                          required
                          className="min-h-[100px]"
                        />
                      </div>
                      <div className="flex gap-3">
                        <Button type="submit" className="bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full">
                          Submit Response
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            setRespondDialogOpen(false);
                            setSelectedReview(null);
                            setResponseText("");
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProviderDashboard;
